
































import Vue, { PropType } from 'vue'
import { ValidationProvider, extend } from 'vee-validate'
import type { Elements, Element, Validator } from '../UiForm.types'
import UiFormField from '../UiFormField'
import UiFormCheckbox from '../UiFormCheckbox'
import UiFormTextarea from '../UiFormTextarea'
import UiFormSelect from '../UiFormSelect'
import UiFormHidden from '../UiFormHidden'
import UiFormFieldset from '../UiFormFieldset'
import UiFormFileUpload from '../UiFormFileUpload'
import * as validators from '../ValidationRules'

export default Vue.extend({
  name: 'UiFormFieldList',
  components: {
    UiFormFieldset,
    ValidationProvider,
    UiFormField,
    UiFormCheckbox,
    UiFormTextarea,
    UiFormSelect,
    UiFormHidden,
    UiFormFileUpload
  },
  props: {
    elements: {
      type: Array as PropType<Elements>,
      required: true
    },
    model: {
      type: Object,
      required: true
    },
    components: {
      type: [Object, Boolean],
      required: false,
      default: false
    },
    classes: {
      type: [Object, Boolean],
      required: false,
      default: false
    },
    extendRules: {
      type: Array,
      default: () => []
    },
    extendTypes: {
      type: Object,
      default: () => ({})
    },
    i18n: {
      type: Object as PropType<Record<string, string>>,
      default: () => ({})
    }
  },
  data () {
    return {
      rules: [] as Validator[],
      availableRules: [
        'NotEmpty',
        'EmailAddress',
        'StringtLength',
        'RegularExpression',
        'FileSize'
      ],
      availableCustomTypes: {
        Honeypot: 'UiFormHidden',
        SingleSelect: 'UiFormSelect'
      }
    }
  },
  computed: {
    customTypes (): Record<string, any> {
      return ({
        ...this.availableCustomTypes,
        ...this.extendTypes
      })
    }
  },
  beforeMount () {
    if (this.elements.length) {
      this.rules = this.getValidationRules(this.elements)
    }

    if (this.rules.length) {
      this.extendValidation(this.rules)
    }
  },
  created () {
    if (this.components) {
      Object.assign(this.$options.components, this.components)
    }
  },
  methods: {
    /**
     * Get recursively all validation rules and designate uniqe
     * @param {Elements} elements Elements array
     * @returns {Validator[]} Array with unique validation rules
     */
    getValidationRules (elements: Elements): Validator[] {
      let rules = [] as Validator[]
      elements.forEach((element: Element) => {
        if (element.elements) {
          rules = rules.concat(this.getValidationRulesByFieldset(element.elements))
          return rules
        }

        rules = rules.concat(this.getValidationRulesByFieldset(elements))
      })

      return [...new Set(rules.map((validator: Validator) => JSON.stringify(validator)))].map((validator: string) => JSON.parse(validator))
    },

    /**
     * Get all validation rules from one fieldset
     * @param {Elements} elements Elements array
     * @returns {Validator[]} Array with validation rules
     */
    getValidationRulesByFieldset (elements: Elements): Validator[] {
      let rules = [] as Validator[]
      elements.forEach((element: Element) => {
        if (element.validators) {
          rules = rules.concat(element.validators)
        }
      })
      return rules
    },

    /**
     * Extend vee-validate with custom validation rules
     * @param {Validator[]} rules Array with validation rules
     */
    extendValidation (rules: Validator[]): void {
      rules.forEach((rule: Validator) => {
        if (typeof validators[rule.identifier] !== 'undefined') {
          extend(rule.identifier, {
            ...validators[rule.identifier],
            message: rule.errorMessage
          })
        }
      })
    },
    /**
     * Get component by field type
     * @param {Element} field single field
     * @returns {String} field component name
     */
    getComponentField (field: Element): string {
      if (Object.keys(this.components).includes(field.identifier)) {
        return field.identifier
      }
      if (Object.keys(this.customTypes).includes(field.type)) {
        return this.customTypes[field.type]
      }
      if (this.$options.components && this.$options.components[`UiForm${field.type}`]) {
        return `UiForm${field.type}`
      }
      return 'UiFormField'
    },
    /**
     * Get css class for single row based on fields
     * @param {Element} field single field
     * @returns {String} css class
     */
    getCssClass (field: Element, classes: object): string|undefined {
      if (classes && field) {
        if (Object.keys(classes).includes(field.identifier)) {
          return classes[field.identifier]
        }
      }
    }
  }
})
